import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { initAuth } from './auth.js';
import { MatterPage, MattersPage } from './pages.js';
import { TodayPage } from './today.js';
import { SfAnswersPage } from './sf.js';
import { TrendsPage } from './trends.js';
import { SignInScreen, STATE_WORDS } from './states.js';
// The design system's tokens (ported 2026-10-18 from the medrec console's 10-17 redesign): every colour, size and radius, once, first, so
// every sheet below reads its roles (and the old names it keeps as aliases). The only sheet that writes a colour (test/raw-hex-guard).
import './styles/console-theme.css';
// The text size's factor on the root element (ported 2026-10-12 from the medrec console, Rod's pick C there): `--ts` for every scaled
// length in the sheets below. The step itself is put on the root before the first paint (index.html) and changed by the header's tool.
import './styles/text-size.css';
// Fonts self-hosted, the medrec console's way (2026-10-11 look match): the OFL files ship inside the build (@fontsource), so the console
// asks no outside server for them. Fira Sans for words and numbers, Fira Code for ids (claim and policy numbers). The condensed face left
// with the design system (2026-10-18, the medrec console's 10-17 redesign: no condensed face, a 12 px floor).
import '@fontsource/fira-sans/400.css';
import '@fontsource/fira-sans/500.css';
import '@fontsource/fira-sans/600.css';
import '@fontsource/fira-sans/700.css';
import '@fontsource/fira-code/400.css';
import '@fontsource/fira-code/500.css';
import '@fontsource/fira-code/600.css';
// Trends and Today wear shadcn's look (Cesar 2026-10-22, mockup v5): Geist, self-hosted like Fira (OFL)
import '@fontsource/geist-sans/400.css';
import '@fontsource/geist-sans/500.css';
import '@fontsource/geist-sans/600.css';
import '@fontsource/geist-sans/700.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import './styles/new-look.css';
import './styles/claims.css';
import './styles/trends-shadcn.css';
import './styles/today-shadcn.css';
// The medrec console's frame (2026-10-11; its 10-17 redesign since 2026-10-18): the rail, the top bar, the controls and the shared states,
// over the two sheets above.
import './styles/medrec-look.css';
// The system is doing the work (ported 2026-10-23 from the medrec console's system-working lane, its PR #340): the work tint and tag on the
// rows the system is handling, and the bar atop their panel; before the list pieces, so the selection's blue still wins on an open row.
import './styles/system-working.css';
// The shared list and timeline pieces (src/list/, the medrec console's console-patterns.css, 2026-10-18): the table, the sortable header,
// the row key, the group heading, the fixed action column, the Salesforce letters, the Since cell, the tag and the in and out marks.
import './styles/console-patterns.css';
// One press, every action button (ported 2026-10-18 from the medrec console's speed-1018 lane, its PR #176): the pressed button's spinner,
// the press dip and Refresh's turn; last, so it wins its ties with no `!important`.
import './styles/press.css';
// The action buttons' tones (ported 2026-10-25 from the medrec console's action-tones lane, its PR #356): each button wears what its press
// does (money red, client amber, the carrier blue, our records green, look or hand off grey); after the press sheet, so a pressed button keeps
// its tone while its press is out.
import './styles/action-tones.css';

/** Sign-in finishes before the first render (the medrec console's rule), so no query fires without a token. */
async function start(): Promise<void> {
  const root = createRoot(document.getElementById('app')!);
  const state = await initAuth();
  // the sign-in box (the medrec console's sign-in screen, 2026-10-18): what is happening, never a bare line at the window's corner
  if (state.kind === 'redirecting') { root.render(<SignInScreen><p className="qs-p">{STATE_WORDS.signingIn}</p></SignInScreen>); return; }
  if (state.kind === 'error') { root.render(<SignInScreen><p className="qs-p">{state.message}</p></SignInScreen>); return; }
  const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });
  root.render(
    <StrictMode>
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/matters" replace />} />
            <Route path="/matters" element={<MattersPage />} />
            <Route path="/matters/:id" element={<MatterPage />} />
            {/* the Open issues page became the Queue's Last 14 days tab (phase 1, Rod 2026-10-13 "1a 2a"); old links and bookmarks land there */}
            <Route path="/issues" element={<Navigate to={{ pathname: '/matters', search: '?tab=d14' }} replace />} />
            <Route path="/today" element={<TodayPage />} />
            <Route path="/state-farm" element={<SfAnswersPage />} />
            <Route path="/trends" element={<TrendsPage />} />
            <Route path="*" element={<Navigate to="/matters" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}
void start();
